import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { PawPrint, EnvelopeSimple, Lock, ArrowRight, Translate } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "Pawfile — Sign in";
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/");
    else setErr(res.error || "Login failed");
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] bg-white">
      {/* Left form */}
      <div className="flex flex-col p-8 lg:p-12 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-pf-primary text-white grid place-items-center shadow-card">
              <PawPrint size={22} weight="fill" />
            </div>
            <span className="font-display font-extrabold text-xl tracking-tight text-pf-text">
              Pawfile
            </span>
          </div>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 rounded-lg border border-pf-border px-2.5 py-1.5 text-xs font-semibold text-pf-text-secondary hover:bg-pf-bg transition-colors"
          >
            <Translate size={14} />
            <span className={lang === "en" ? "text-pf-primary" : ""}>EN</span>
            <span className="opacity-40">/</span>
            <span className={lang === "ro" ? "text-pf-primary" : ""}>RO</span>
          </button>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <h1 className="text-4xl sm:text-5xl font-display font-extrabold text-pf-text tracking-tight leading-tight">
              {t("signIn")}.
            </h1>
            <p className="mt-2 text-pf-text-secondary text-sm">{t("signInHint")}</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <label className="pf-label mb-1.5 block">{t("email")}</label>
                <div className="relative">
                  <EnvelopeSimple
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary"
                  />
                  <input
                    data-testid="login-email-input"
                    type="email"
                    required
                    className="pf-input pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
              </div>
              <div>
                <label className="pf-label mb-1.5 block">{t("password")}</label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary"
                  />
                  <input
                    data-testid="login-password-input"
                    type="password"
                    required
                    className="pf-input pl-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              {err && (
                <div data-testid="login-error" className="text-sm text-pf-danger bg-pf-danger-bg border border-pf-danger/20 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}

              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={busy}
                className="pf-btn-primary w-full justify-center py-2.5"
              >
                {busy ? t("signingIn") : t("signIn")} <ArrowRight size={16} />
              </button>

              <div className="text-center space-y-2">
                <a href="/forgot-password" data-testid="forgot-password-link" className="text-xs text-pf-text-secondary hover:text-pf-primary hover:underline block">
                  {t("forgotPassword")}
                </a>
                <div className="text-xs text-pf-text-secondary">
                  {t("noAccountYet")}{" "}
                  <a href="/register" data-testid="register-link" className="text-pf-primary font-medium hover:underline">
                    {t("createAccount")}
                  </a>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="text-xs text-pf-text-secondary">
          © {new Date().getFullYear()} Pawfile • Veterinary practice management
        </div>
      </div>

      {/* Right image */}
      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1682448169828-039868860001?auto=format&fit=crop&w=1400&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#17252A]/70 via-[#17252A]/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <blockquote className="max-w-md">
            <div className="font-display text-2xl font-semibold leading-snug">
              &ldquo;The kindest thing you can do for a patient is remember every detail of their story.&rdquo;
            </div>
            <div className="mt-3 text-sm text-white/80">Every record. Every visit. Every paw.</div>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
